import React, { createContext, useContext, useEffect, useState } from 'react';
import { useScannerStore } from '../stores/scannerStore';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Scanner context
const ScannerContext = createContext();

// Global scanner configuration keys
const CONFIG_KEYS = {
  HAPTICS_ENABLED: '@scanner:haptics_enabled',
  SOUND_ENABLED: '@scanner:sound_enabled',
  THEME: '@scanner:theme',
  PERFORMANCE_MODE: '@scanner:performance_mode',
};

export const QRScannerProvider = ({ children }) => {
  const scannerStore = useScannerStore();

  // Global configuration state
  const [globalConfig, setGlobalConfig] = useState({
    enableHaptics: true,
    enableSound: false,
    theme: 'dark',
    performanceMode: 'high', // 'high' | 'balanced' | 'battery-saver'
    cacheEnabled: true,
    debugMode: false,
  });

  // Load saved configuration on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const savedConfig = await AsyncStorage.multiGet([
          CONFIG_KEYS.HAPTICS_ENABLED,
          CONFIG_KEYS.SOUND_ENABLED,
          CONFIG_KEYS.THEME,
          CONFIG_KEYS.PERFORMANCE_MODE,
        ]);

        const config = {};
        savedConfig.forEach(([key, value]) => {
          if (value !== null) {
            switch (key) {
              case CONFIG_KEYS.HAPTICS_ENABLED:
                config.enableHaptics = value === 'true';
                break;
              case CONFIG_KEYS.SOUND_ENABLED:
                config.enableSound = value === 'true';
                break;
              case CONFIG_KEYS.THEME:
                config.theme = value;
                break;
              case CONFIG_KEYS.PERFORMANCE_MODE:
                config.performanceMode = value;
                break;
            }
          }
        });

        setGlobalConfig((prev) => ({ ...prev, ...config }));
      } catch (error) {
        console.error('Failed to load scanner config:', error);
      }
    };

    loadConfig();
  }, []);

  // Update configuration
  const updateConfig = async (key, value) => {
    try {
      // Update local state
      setGlobalConfig((prev) => ({ ...prev, [key]: value }));

      // Save to storage
      let storageKey;
      switch (key) {
        case 'enableHaptics':
          storageKey = CONFIG_KEYS.HAPTICS_ENABLED;
          break;
        case 'enableSound':
          storageKey = CONFIG_KEYS.SOUND_ENABLED;
          break;
        case 'theme':
          storageKey = CONFIG_KEYS.THEME;
          break;
        case 'performanceMode':
          storageKey = CONFIG_KEYS.PERFORMANCE_MODE;
          break;
        default:
          return;
      }

      await AsyncStorage.setItem(storageKey, String(value));
    } catch (error) {
      console.error('Failed to save scanner config:', error);
    }
  };

  // Get performance settings based on mode
  const getPerformanceSettings = () => {
    switch (globalConfig.performanceMode) {
      case 'high':
        return {
          fps: 30,
          resolution: 'high',
          processingDelay: 100,
          cacheEnabled: true,
        };
      case 'balanced':
        return {
          fps: 15,
          resolution: 'medium',
          processingDelay: 200,
          cacheEnabled: true,
        };
      case 'battery-saver':
        return {
          fps: 10,
          resolution: 'low',
          processingDelay: 300,
          cacheEnabled: false,
        };
      default:
        return {
          fps: 15,
          resolution: 'medium',
          processingDelay: 200,
          cacheEnabled: true,
        };
    }
  };

  // Analytics tracking
  const trackScanEvent = (eventName, data = {}) => {
    if (globalConfig.debugMode) {
    }

    // Send to analytics service
    // analytics.track(eventName, {
    //   ...data,
    //   mode: scannerStore.mode,
    //   performanceMode: globalConfig.performanceMode,
    //   timestamp: Date.now()
    // });
  };

  // Error reporting
  const reportError = (error, context = {}) => {
    console.error('[Scanner Error]', error, context);

    // Send to error tracking service
    // errorTracker.captureException(error, {
    //   tags: {
    //     component: 'qr-scanner',
    //     mode: scannerStore.mode
    //   },
    //   extra: context
    // });
  };

  // Context value
  const contextValue = {
    // Store state and actions
    ...scannerStore,

    // Global configuration
    config: globalConfig,
    updateConfig,
    getPerformanceSettings,

    // Utilities
    trackScanEvent,
    reportError,

    // Feature flags
    features: {
      torchEnabled: true,
      galleryEnabled: true,
      batchScanEnabled: false,
      offlineModeEnabled: false,
    },
  };

  return (
    <ScannerContext.Provider value={contextValue}>
      {children}
    </ScannerContext.Provider>
  );
};

// Hook to use scanner context
export const useScanner = () => {
  const context = useContext(ScannerContext);
  if (!context) {
    throw new Error('useScanner must be used within QRScannerProvider');
  }
  return context;
};

// Convenience hooks for specific features
export const useScannerConfig = () => {
  const { config, updateConfig } = useScanner();
  return { config, updateConfig };
};

export const useScannerAnalytics = () => {
  const { trackScanEvent } = useScanner();
  return trackScanEvent;
};

export const useScannerPerformance = () => {
  const { getPerformanceSettings, config } = useScanner();
  return {
    settings: getPerformanceSettings(),
    mode: config.performanceMode,
  };
};
